import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Grid, Link, Paper, Stack, Typography } from "@mui/material"
import PersonIcon from '@mui/icons-material/Person';
import { OpenAlexReferenceData, RetractedWork, RiskScoreAnalysis } from "./CommonInterfaces";
import LinkIcon from '@mui/icons-material/Link';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ImportContactsIcon from '@mui/icons-material/ImportContacts';
import ArticleTitle from "./ArticleTitle";
import TrafficIcon from '@mui/icons-material/Traffic';
import DoiPanel from "./DoiPanel";
import { useState } from "react";

interface RiskScoreProps {
    data: RiskScoreAnalysis,
    problematicReferences: OpenAlexReferenceData[],
    retractionData?: RetractedWork | undefined,
    newRetractions: string[]
}

function RiskScorePanel({ data, problematicReferences, newRetractions }: RiskScoreProps) {

    const [weHaveCheckedRetractions, setWeHaveCheckedRetractions] = useState(false);
    const [retractionNature, setRetractionNature] = useState('not-retracted');


    return <>
        <Paper elevation={0} sx={{ p: 2, mb: 2 }} style={{ backgroundColor: '#d8dceb' }}>
            <Grid
                container
                direction="row"
                justifyContent="space-around"
                alignItems="stretch"
                rowSpacing={2}
            >
                <Grid item xs={12} key="authors">
                    <Typography variant="caption" sx={{
                        mr: 1,
                        mt: 1
                    }}>Authors:</Typography>
                    {data.authorships.map((author, i) =>
                        <Chip key={"authors_" + i}
                            label={author.rawAuthorName + " (" + author.institutions.map(institution => institution.displayName) + ")"}
                            variant="outlined"
                            color="info"
                            size="small"
                            sx={{
                                mr: 1,
                                mt: 1,
                                backgroundColor: '#FFFFFF'
                            }}
                        />
                    )}
                </Grid>
                <Grid item xs={12} key="source-data">
                    <Typography variant="caption" sx={{
                        mr: 1,
                        mt: 1
                    }}>Source data:</Typography>
                    <Link href={data.doi} target="_blank"><Chip icon={<FingerprintIcon />} label={data.doi} variant="outlined" sx={{
                        mr: 1,
                        mt: 1,
                        backgroundColor: '#FFFFFF'
                    }} /></Link>
                    <Chip icon={<CalendarMonthIcon />} label={"Publication date:" + data.publicationDate} variant="outlined" sx={{
                        mr: 1,
                        mt: 1,
                        backgroundColor: '#FFFFFF'
                    }} />
                    <Chip icon={<ImportContactsIcon />} label={"Journal: " + data.source + "/" + data.publisherName} variant="outlined" sx={{
                        mr: 1,
                        mt: 1,
                        backgroundColor: '#FFFFFF'
                    }} />
                </Grid>
            </Grid>
        </Paper>
        <Paper elevation={0} sx={{ p: 2, mb: 2 }} style={{ backgroundColor: '#f2f4f7' }}>
            <Typography variant="caption" sx={{
                mr: 1,
                mt: 1,
                mb: 2
            }}>Our evaluation:</Typography>
            <Box>
                {(data.isAlreadyRetracted) &&
                    <Chip icon={<TrafficIcon />} label="Retracted article" color="error" />
                }
                {(!data.isAlreadyRetracted && data.isRetractionNote) &&
                    <Chip icon={<TrafficIcon />} label="Retraction notice" color="error" />
                }
                {weHaveCheckedRetractions && retractionNature === "Expression of concern"
                    &&
                    <Chip icon={<TrafficIcon />} label="Expression of concern" color="warning" sx={{ mr: 1 }} />}
                {weHaveCheckedRetractions && retractionNature === "not-retracted"
                    && !data.isAlreadyRetracted && !data.isRetractionNote &&
                    data.scitilityRiskScore < 0.5 &&
                    <Chip icon={<TrafficIcon />} label="Low risk" color="success" sx={{ mr: 1 }} />}
                {weHaveCheckedRetractions && retractionNature === "not-retracted" &&
                    !data.isAlreadyRetracted && !data.isRetractionNote &&
                    data.scitilityRiskScore >= 0.5 && data.scitilityRiskScore < 1.5 &&
                    <Chip icon={<TrafficIcon />} label="Medium Risk" color="warning" sx={{ mr: 1 }} />}
                {weHaveCheckedRetractions && retractionNature === "not-retracted" &&
                    !data.isAlreadyRetracted && !data.isRetractionNote &&
                    data.scitilityRiskScore >= 1.5 &&
                    <Chip icon={<TrafficIcon />} label="High risk" color="error" sx={{ mr: 1 }} />}
                {data.totalNumberOfDifferentHighRiskRetractions > 0 && <Chip icon={<PersonSearchIcon />} label={

                    data.numberProblematicAuthors + (data.numberProblematicAuthors === 1 ? " author " : " authors ")
                    + "with "
                    + data.totalNumberOfDifferentHighRiskRetractions
                    + " unique High Risk Retractions + EoC"
                }
                    color={(data.numberProblematicAuthors === 0) ? "success" : "error"}
                    variant="outlined" sx={{
                        mr: 1,
                        ...(data.numberProblematicAuthors === 0 && {
                            color: 'success'
                        }),
                        ...(data.numberProblematicAuthors > 0 && {
                            color: 'error'
                        })
                    }} />}
                {data.totalNumberOfDifferentLowRiskRetractions > 0 &&
                    <Chip icon={<PersonSearchIcon />} label={
                        data.authorAnalysis.filter(authorAnalysis =>
                            authorAnalysis.problemsFound.filter(problem => !problem.highRisk).length > 0
                        ).length
                        + " authors with " + data.totalNumberOfDifferentLowRiskRetractions + " unique Lower Risk Retractions + EoC"
                    }
                        color="warning"
                        variant="outlined" sx={{
                            mr: 1
                        }} />
                }

                <Chip icon={<LinkIcon />} label={data.numberProblematicReferences + " retracted references"}
                    color={(data.numberProblematicReferences === 0) ? "success" : "error"}
                    variant="outlined" sx={{
                        mr: 1,
                        ...(data.numberProblematicReferences === 0 && {
                            color: 'success'
                        }),
                        ...(data.numberProblematicReferences > 0 && {
                            color: 'error'
                        })
                    }} />
            </Box>
        </Paper>

        <DoiPanel partialDoiProp={data.doi.replaceAll("https://doi.org/", "")}
            updateFunction={(retractionNature: string) => {
                setRetractionNature(retractionNature);
                setWeHaveCheckedRetractions(true)
            }} />

        {data.authorAnalysis.length > 0 && <Paper elevation={0} sx={{ p: 2, mb: 2 }} style={{ backgroundColor: '#f5f7ff' }}>
            {/** TODO : Chage this by a Chip Array */}
            {data.authorAnalysis.length > 0 && <Typography variant="caption">{data.authorAnalysis.length} out of {data.authorships.length} authors of this article have a history of retractions / expressions of concern: </Typography>}
            <Stack marginX={{ xs: 0, md: 2 }} key="problems_accordions">
                {
                    data.authorAnalysis.sort((a, b) => b.problemsFound.length - a.problemsFound.length).map((author, j) =>
                        <Accordion key={"problem_accordion_" + j} style={{ marginBottom: 10 }} elevation={0} sx={{ '&:before': { height: '0px' } }}>
                            <AccordionSummary expandIcon={<ArrowDropDownIcon key={"accordion_summary_icon_" + j} />} key={"accordion_summary_" + j}>
                                <Stack direction={{ xs: 'column', md: 'row' }} key={"stack_problem_" + j} >
                                    <Stack direction='row' key={"stack_problem_a_" + j}>
                                        <PersonIcon fontSize="small" key={"icon_person_" + j} />
                                        <Typography variant="body2" key={"name_person_" + j} style={{ alignContent: "center", verticalAlign: "center", margin: 4 }}>{author.authorDisplayName}</Typography>
                                    </Stack>
                                    <Stack direction='row' key={"stack_problem_b_" + j}>
                                        {author.problemsFound
                                            .filter(x => x.retractionNature === "Retraction" && x.highRisk).length > 0 &&
                                            <Chip color="error" key={"error_chip_" + j}
                                                label={author.problemsFound
                                                    .filter(x => x.retractionNature === "Retraction" && x.highRisk).length + " high risk retractions"}
                                                sx={{
                                                    mr: 1,
                                                    color: 'white'
                                                }}
                                            ></Chip>}
                                        {author.problemsFound
                                            .filter(x => x.retractionNature === "Retraction" && !x.highRisk).length > 0 &&
                                            <Chip color="warning" key={"warning_chip_" + j}
                                                label={author.problemsFound
                                                    .filter(x => x.retractionNature === "Retraction" && !x.highRisk).length + " lower risk retractions"}
                                                sx={{
                                                    mr: 1,
                                                    color: 'white'
                                                }}
                                            ></Chip>}
                                        {author.problemsFound
                                            .filter(x => x.retractionNature === "Expression of concern" && x.highRisk).length > 0 &&
                                            <Chip color="error" key={"eoc_chip_" + j} label={author.problemsFound
                                                .filter(x => x.retractionNature === "Expression of concern" && x.highRisk).length + " high risk expressions of concern"}
                                                sx={{
                                                    mr: 1,
                                                    color: 'white'
                                                }}
                                            ></Chip>
                                        }
                                        {author.problemsFound
                                            .filter(x => x.retractionNature === "Expression of concern" && !x.highRisk).length > 0 &&
                                            <Chip color="warning" key={"eoc_chip_" + j} label={author.problemsFound
                                                .filter(x => x.retractionNature === "Expression of concern" && !x.highRisk).length + " lower risk expressions of concern"}
                                                sx={{
                                                    mr: 1,
                                                    color: 'white'
                                                }}
                                            ></Chip>
                                        }
                                        {author.problemsFound.map(p => p.recordId).filter(record => newRetractions.includes(record)).length > 0 &&
                                            <Chip label="Updated in selected timeframe" key={"updated_chip_" + j} color="info" sx={{
                                                mr: 1,
                                                color: 'white'
                                            }} />
                                        }
                                    </Stack>
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails>
                                {author.problemsFound.sort((a, b) =>
                                    a.retractionNature.charAt(0) > b.retractionNature.charAt(0) ? -1 : 1
                                ).map(problem =>
                                    <>
                                        <Stack direction="row">

                                            <Chip label={problem.retractionNature} color={problem.highRisk ? "error" : "warning"} sx={{
                                                mr: 2,
                                                color: 'white'
                                            }} />

                                            {newRetractions.indexOf(problem.recordId) > -1 &&
                                                <Chip label="Updated in selected timeframe" color="info" sx={{
                                                    mr: 1,
                                                    color: 'white'
                                                }} />}

                                            {problem.recordId !== '0' &&
                                                <Link href={"/argos/doi/" + problem.originalPaperDoi.replace('https://doi.org/', '')
                                                } target="_blank">
                                                    <Typography variant="body2" sx={{ mb: 2 }}>{problem.title}</Typography>
                                                </Link>
                                            }
                                            {problem.recordId === '0' &&
                                                <Link href={problem.originalPaperDoi} target="_blank">
                                                    <Typography variant="body2" sx={{ mb: 2 }}>{problem.title}</Typography>
                                                </Link>
                                            }
                                        </Stack>
                                    </>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    )
                }
            </Stack>
        </Paper>}

        {problematicReferences.length > 0 && <Paper elevation={0} sx={{ p: 2, mb: 2 }} style={{ backgroundColor: '#f2f4f7' }}>
            {(!data.isAlreadyRetracted && data.isRetractionNote) &&
                <Typography variant="caption">This note retracts the following articles:</Typography>
            }
            {(!data.isRetractionNote) &&
                <Typography variant="caption">{problematicReferences.length} retracted references:</Typography>
            }
            {
                problematicReferences.map(ref =>
                    <Stack direction='row' marginBottom={3} marginTop={3} marginX={{ md: 10 }}>
                        <Chip icon={<LinkIcon />} style={{ marginRight: 10 }} sx={{ display: { xs: "flex", md: "none" } }} />
                        <Chip icon={<LinkIcon />} label={(!data.isAlreadyRetracted && data.isRetractionNote) ? "Retracted article" : ("Retracted " + (ref.retractionDate != null ? (isFirstDateAfter(data.publicationDate, ref.retractionDate) ? " Pre publication " : " Post publication ") + "(" + ref.retractionDate + ")" : ""))} style={{ marginRight: 50 }} sx={{ display: { xs: "none", md: "flex" } }} />
                        <Typography variant="body2">
                            <Link href={"/argos/doi/" + ref.doi.replace('https://doi.org/', '')} target="_blank">
                                <ArticleTitle title={ref.title} />
                            </Link>
                        </Typography>
                    </Stack>

                )
            }
        </Paper>
        }
    </>
}

function isFirstDateAfter(date1: string, date2: string): boolean {
    return new Date(date1) > new Date(date2);
}

export default RiskScorePanel
export type { RiskScoreAnalysis }